import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import msgRequest from "@/api/msg-request";
import { getFacilityKey, getPatientKey } from "@/store/profile";
import { useMessageStore } from "@/store/messages";
import { cn } from "@/utils/cn";
import { SubmitButton } from "@/components/ui/SubmitButton";

const createMsgSchema = z.object({
  subject: z.string().min(1, "Subject is required").max(100),
  message: z.string().min(1, "Message is required").max(8000),
});

type CreateMsgForm = z.infer<typeof createMsgSchema>;

type NewMessageFormProps = {
  className?: string;
  onCancel: () => void;
};

export function NewMessageForm({ className, onCancel }: NewMessageFormProps) {
  const bumpMessageList = useMessageStore((s) => s.bumpMessageList);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CreateMsgForm>({
    resolver: zodResolver(createMsgSchema),
    defaultValues: { subject: "", message: "" },
  });

  const onSubmit = async (data: CreateMsgForm) => {
    const facilityKey = getFacilityKey();
    const patientKey = getPatientKey();
    if (!facilityKey || !patientKey) return;
    try {
      await msgRequest.request.insertMessage(facilityKey, patientKey, {
        subject: data.subject,
        message: data.message,
      });
      toast.success("Message sent");
      bumpMessageList();
      onCancel();
      reset();
    } catch {
      toast.error("Failed to send message");
    }
  };

  return (
    <div className={cn("flex min-h-0 min-w-0 flex-col", className)}>
      <h2
        id="create-msg-title"
        className="shrink-0 text-lg font-semibold text-healthsource-dark"
      >
        New Message
      </h2>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="mt-4 flex min-h-0 min-w-0 flex-1 flex-col space-y-4"
      >
        <div>
          <label
            htmlFor="msg-subject"
            className="block text-sm font-medium"
          >
            Subject
          </label>
          <input
            id="msg-subject"
            type="text"
            maxLength={100}
            {...register("subject")}
            className="mt-1 min-h-11 w-full min-w-0 max-w-full rounded-md border border-healthsource-lightLavender bg-white px-3 py-2 text-base text-healthsource-dark"
          />
          {errors.subject && (
            <p className="mt-1 text-sm text-destructive">
              {errors.subject.message}
            </p>
          )}
        </div>
        <div>
          <label
            htmlFor="msg-body"
            className="block text-sm font-medium"
          >
            Message
          </label>
          <textarea
            id="msg-body"
            rows={6}
            maxLength={8000}
            {...register("message")}
            className="mt-1 w-full min-w-0 max-w-full min-h-0 flex-1 rounded-md border border-healthsource-lightLavender bg-white px-3 py-2 text-base text-healthsource-dark [overflow-wrap:anywhere] md:min-h-[200px]"
          />
          {errors.message && (
            <p className="mt-1 text-sm text-destructive">
              {errors.message.message}
            </p>
          )}
        </div>
        <div className="mt-auto flex flex-wrap justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="min-h-11 rounded-md border border-healthsource-lightLavender bg-white px-4 py-2 text-sm font-semibold text-healthsource-dark hover:bg-healthsource-lightGray/60"
          >
            Cancel
          </button>
          <SubmitButton isSubmitting={isSubmitting}>Send</SubmitButton>
        </div>
      </form>
    </div>
  );
}
